import * as RadixSelect from '@radix-ui/react-select'
import { type ReactNode, useRef, useState } from 'react'
import { defineMessages, useT } from '../i18n/index.ts'
import { cn } from '../lib/cn.ts'
import { FieldShell } from './Field.tsx'
import { IconCaret, IconCheck } from './Icon.tsx'
import { useLayerHost } from './layerHost.ts'

/**
 * The app's only dropdown.
 *
 * A native `<select>` is rendered by the operating system, so it ignores every token in this
 * kit: its own font, its own row height, a chrome-blue highlight, and on Windows a menu that
 * looks nothing like the page. On the matching editor there is one of these per row, which
 * made the densest screen in the product the least finished-looking.
 *
 * Radix gives the behaviour a hand-rolled menu always gets wrong -- typeahead, arrow keys,
 * Escape, focus return, scroll locking, collision flipping -- and leaves the painting to us.
 */

const messages = defineMessages({
	en: { choose: 'Choose…' },
	ro: { choose: 'Alege…' },
	ru: { choose: 'Выберите…' },
	fr: { choose: 'Choisir…' },
	it: { choose: 'Scegli…' },
	kk: { choose: 'Таңдаңыз…' },
	es: { choose: 'Elige…' },
	pt: { choose: 'Escolha…' },
	de: { choose: 'Auswählen…' },
})

const CLEAR = '__clear'

// Wraps rather than truncating: a long option stays readable, and the row grows to fit it.
const item =
	'relative flex min-h-11 cursor-pointer select-none items-center gap-2 rounded-input py-2 ' +
	'pl-9 pr-3 text-body text-ink outline-none [overflow-wrap:anywhere] ' +
	'data-[highlighted]:bg-teal-50 data-[state=checked]:font-[560] data-[state=checked]:text-teal-800'

export type Option = { value: string; label: string }

export function Select({
	id,
	value,
	onValueChange,
	options,
	placeholder,
	label,
	disabled,
	className,
	triggerClassName,
	valueLabel,
	clearLabel,
	invalid,
	describedBy,
}: {
	/** Required, like every interactive element here. */
	id: string
	value: string
	onValueChange: (value: string) => void
	options: Option[]
	placeholder?: string
	/** Used when no visible `<label>` points at this control. */
	label?: string
	disabled?: boolean
	className?: string
	triggerClassName?: string
	/** What the closed trigger shows, when it should be shorter than the option's label. */
	valueLabel?: ReactNode
	/** Adds a first item that puts the select back to unpicked (`onValueChange('')`). */
	clearLabel?: string
	/** The pick is wrong or missing: announced, and painted by the caller through `aria-invalid:` classes. */
	invalid?: boolean
	/** Id of the line that says what is wrong with it. */
	describedBy?: string
}) {
	const t = useT(messages)
	// Tapping the trigger of an open menu must close it. Radix closes on the pointerdown (outside
	// the menu), then the same tap's click lands on the trigger and opens it again: on a phone the
	// menu looked stuck open (reported 2026-10-06). A reopen within one tap of a close is that echo.
	const [open, setOpen] = useState(false)
	const layer = useLayerHost()
	const closedAt = useRef(0)
	const onOpenChange = (next: boolean) => {
		if (next && performance.now() - closedAt.current < 400) return
		if (!next) closedAt.current = performance.now()
		setOpen(next)
	}
	return (
		<RadixSelect.Root
			value={value}
			// Inside a <form>, Radix keeps a hidden native <select> in step and, when the value moves to
			// an option that renders in the same commit (a folder made from the picker), reports "" from
			// it. No item has an empty value, and a real clear arrives as CLEAR, so "" is never a pick.
			onValueChange={(v) => v !== '' && onValueChange(v === CLEAR ? '' : v)}
			disabled={disabled}
			open={open}
			onOpenChange={onOpenChange}
		>
			<RadixSelect.Trigger
				ref={layer.ref}
				id={id}
				aria-label={label}
				aria-invalid={invalid || undefined}
				aria-describedby={describedBy}
				className={cn(
					'inline-flex h-12 items-center justify-between gap-2 rounded-input border border-line bg-sheet px-3.5',
					'text-body text-ink transition-[border-color,box-shadow] duration-(--dur-instant)',
					'hover:border-line-strong focus-visible:outline-none focus:border-teal-700 focus:shadow-[0_0_0_1px_var(--color-teal-700)]',
					'data-[state=open]:border-teal-700 disabled:bg-canvas-deep disabled:text-ink-2 data-[placeholder]:text-ink-2',
					className,
					triggerClassName,
				)}
			>
				{/* Flex, not inline: a rich valueLabel (icon + code) centres on the box, not on the text baseline. */}
				<span className="flex min-w-0 items-center">
					<RadixSelect.Value className="min-w-0 truncate" placeholder={placeholder ?? t.choose}>
						{valueLabel}
					</RadixSelect.Value>
				</span>
				<RadixSelect.Icon className="shrink-0 text-ink-2">
					<IconCaret className="size-4" />
				</RadixSelect.Icon>
			</RadixSelect.Trigger>

			<RadixSelect.Portal container={layer.host}>
				{/*
				 * `position="popper"` so a menu near the bottom of a long editor flips upward
				 * instead of being clipped. The width follows the trigger, which keeps a column of
				 * these aligned rather than each one sized to its longest option.
				 */}
				<RadixSelect.Content
					position="popper"
					sideOffset={6}
					collisionPadding={8}
					className="z-(--z-menu) max-h-72 min-w-[min(var(--radix-select-trigger-width),var(--radix-select-content-available-width))] max-w-[var(--radix-select-content-available-width)] overflow-hidden
						rounded-btn bg-sheet shadow-3 animate-fade-in"
				>
					<RadixSelect.Viewport className="p-1">
						{clearLabel && value !== '' && (
							<RadixSelect.Item value={CLEAR} className={cn(item, 'text-ink-2')}>
								<RadixSelect.ItemText>{clearLabel}</RadixSelect.ItemText>
							</RadixSelect.Item>
						)}
						{options.map((option) => (
							<RadixSelect.Item key={option.value} value={option.value} className={item}>
								<RadixSelect.ItemIndicator className="absolute left-2.5 inline-flex text-teal-700">
									<IconCheck className="size-4" />
								</RadixSelect.ItemIndicator>
								<RadixSelect.ItemText>{option.label}</RadixSelect.ItemText>
							</RadixSelect.Item>
						))}
					</RadixSelect.Viewport>
				</RadixSelect.Content>
			</RadixSelect.Portal>
		</RadixSelect.Root>
	)
}

/** The labelled form-field version, matching `TextField`'s shell. The app's only SelectField. */
export function SelectField({
	id,
	label,
	hint,
	error,
	className,
	...rest
}: Omit<Parameters<typeof Select>[0], 'className' | 'label'> & {
	label: string
	hint?: string
	error?: string
	className?: string
}) {
	return (
		<FieldShell id={id} label={label} hint={hint} error={error} className={className}>
			<Select id={id} label={label} className="w-full" {...rest} />
		</FieldShell>
	)
}
